import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

export default function Privacy() {
  const [activeSection, setActiveSection] = useState('dataansvarlig-databehandler');

  const sections = [
    { id: 'dataansvarlig-databehandler', title: 'Rollen som dataansvarlig og databehandler' },
    { id: 'kontaktoplysninger', title: 'Kontaktoplysninger' },
    { id: 'behandling-personoplysninger', title: 'Behandling af personoplysninger' },
    { id: 'elektroniske-henvendelser', title: 'Elektroniske henvendelser' },
    { id: 'kunder', title: 'Kunder' },
    { id: 'leverandoerer', title: 'Leverandører' },
    { id: 'jobansoegere', title: 'Jobansøgere' },
    { id: 'modtagere-personoplysninger', title: 'Modtagere af personoplysninger' },
    { id: 'dine-rettigheder', title: 'Dine rettigheder' },
    { id: 'klage-datatilsynet', title: 'Klage til Datatilsynet' },
  ];

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="py-12 bg-stone-50 min-h-screen">
      <Helmet>
        <title>Privatlivspolitik - Campen Camping</title>
        <meta name="description" content="Læs vores privatlivspolitik for at forstå, hvordan vi behandler og beskytter dine personoplysninger hos Campen Camping." />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
            Persondatapolitik
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#0f0d0e] mt-1 mb-4">
            Privatlivspolitik
          </h1>
          <p className="text-stone-600 text-sm md:text-base leading-relaxed">
            Privatlivspolitikken er sidst opdateret d. 23. februar 2026.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          
          {/* Sidebar Navigation - Sticky on desktop */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
              <h3 className="text-stone-900 font-serif font-bold text-base mb-4 pb-3 border-b border-stone-100">
                Indholdsfortegnelse
              </h3>
              <nav className="flex flex-col gap-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`text-left py-2 px-3 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 ${
                      activeSection === section.id
                        ? 'bg-blue-50 text-[#67aedd] pl-4 border-l-2 border-[#67aedd]'
                        : 'text-stone-600 hover:text-[#67aedd] hover:bg-stone-50'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Document Content */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-stone-200/80 p-8 md:p-12 shadow-sm prose prose-stone max-w-none">
            
            {/* Rollen som dataansvarlig og databehandler */}
            <section id="dataansvarlig-databehandler" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Rollen som dataansvarlig og databehandler
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Campen Camping er dataansvarlig når vi behandler dine kontaktoplysninger når du er kunde hos os eller henvender dig til os. I denne situation bliver der indgået en databehandleraftale mellem Campen Camping og kunden. Denne privatlivspolitik omhandler vores behandling af personoplysninger når vi er dataansvarlige.
                </p>
              </div>
            </section>

            {/* Kontaktoplysninger */}
            <section id="kontaktoplysninger" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Kontaktoplysninger
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-200/60 not-italic text-sm text-stone-700 space-y-2">
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">Firmanavn</span>
                    <p className="font-semibold text-stone-900">F Campen v/Finn Campen</p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">CVR.</span>
                    <p className="font-semibold text-stone-900">33438516</p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">Adresse</span>
                    <p className="font-semibold text-stone-900">Vestermarksvej 72, 8305 Samsø</p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">Telefon</span>
                    <p className="font-semibold text-stone-900">
                      <a href="tel:+4551540023" className="hover:text-[#67aedd] transition-colors">
                        +45 51540023
                      </a>
                    </p>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-stone-500 uppercase tracking-wider">E-mail</span>
                    <p className="font-semibold text-stone-900">
                      <a href="mailto:info@campencamping.dk" className="hover:text-[#67aedd] transition-colors">
                        info@campencamping.dk
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Behandling af personoplysninger */}
            <section id="behandling-personoplysninger" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Behandling af personoplysninger
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Vi indsamler kun de personoplysninger som du selv giver os.
                </p>
                <p>
                  Personoplysninger anvendes ikke til profilering eller i forbindelse med automatiske afgørelser.
                </p>
              </div>
            </section>

            {/* Elektroniske henvendelser */}
            <section id="elektroniske-henvendelser" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Elektroniske henvendelser
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Hvis du henvender dig til os via telefon, e-mail eller direkte meddelelser er det nødvendigt at vi gemmer dine kontaktinformationer, med det formål at kunne vende tilbage på din henvendelse.
                </p>
                <p>
                  <strong>Retsgrundlaget</strong> for behandlingen er databeskyttelsesforordningen artikel 6, stk. 1, litra f – legitime interesse. Det er vores vurdering at det både er i vores og din interesse at vi kan besvare dine henvendelser. Du har ret til at bede os om at slette dine kontaktoplysninger efter du har henvendt dig til os.
                </p>
                <p>
                  <strong>Kategorierne af oplysninger</strong> afhænger af hvordan du har henvendt dig til os, men der vil typisk være tale om navn, telefonnummer og e-mail.
                </p>
                <p>
                  Såfremt du henvender dig til os og vi ikke efterfølgende indgår en aftale, så sletter vi dine oplysninger efter 12 måneder.
                </p>
              </div>
            </section>

            {/* Kunder */}
            <section id="kunder" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Kunder
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Når du er kunde hos os eller medarbejder hos en af vores kunder kan det være nødvendigt at vi behandler en række oplysninger om dig, med det formål at levere den aftalte ydelse og afregne.
                </p>
                <p>
                  <strong>Kategorierne af oplysninger</strong> består af navn, adresse, telefonnummer og e-mail og betalingsoplysninger.
                </p>
                <p>
                  <strong>Retsgrundlaget</strong> for behandlingen er databeskyttelsesforordningens artikel 6, stk. 1, litra b hvor behandlingen er nødvendig for opfyldelse af den kontrakt vi har indgået. Vi gemmer dine kontaktoplysninger så længe kundeforholdet består. Personoplysninger til brug for bogføringsloven opbevares indtil 5 år efter udløbet af regnskabsåret, jf. bogføringslovens § 10 og slettes herefter.
                </p>
              </div>
            </section>

            {/* Leverandører */}
            <section id="leverandoerer" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Leverandører
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Når du er leverandør til os er det nødvendigt at vi behandler en række oplysninger om dig, med det formål at modtage den aftalte ydelse og afregne.
                </p>
                <p>
                  <strong>Kategorierne af oplysninger</strong> består af navn, adresse, telefonnummer, e-mail og betalingsoplysninger.
                </p>
                <p>
                  <strong>Retsgrundlaget</strong> for behandlingen er databeskyttelsesforordningens artikel 6, stk. 1, litra b hvor behandlingen er nødvendig for opfyldelse af den kontrakt vi har indgået.
                </p>
                <p>
                  Vi gemmer dine kontaktoplysninger så længe leverandørforholdet består. Personoplysninger til brug for bogføringsloven opbevares indtil 5 år efter udløbet af regnskabsåret, jf. bogføringslovens § 10 og slettes herefter.
                </p>
              </div>
            </section>

            {/* Jobansøgere */}
            <section id="jobansoegere" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Jobansøgere
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Når du sender os en jobansøgning, behandler vi den med det formål at vurdere ansøgningen i forhold til et en stilling hos os.
                </p>
                <p>
                  <strong>Retsgrundlaget</strong> for den behandling sker baseret på databeskyttelsesforordningens artikel 6, stk. 1, litra f – legitime interesse. Det er vores vurdering at det er både i vores og din interesse at vi vurderer din ansøgning i forhold til en stilling hos os.
                </p>
                <p>
                  Vi behandler de <strong>kategorier af personoplysninger</strong> som du selv angiver i din ansøgning, samt generelle CV informationer som uddannelse, erfaring mv., som naturligt forventes at indgå i et CV.
                </p>
                <p>
                  Personoplysninger der behandles i forbindelse med jobansøgninger slettes så snart vi har sendt et afslag afsted til dig. Såfremt vi ønsker at gemme din ansøgning med henblik på en fremtidig jobmulighed, så beder vi om dit samtykke hertil. Hvis vi indgår en aftale om et ansættelsesforhold gemmer vi din ansøgning og CV i vores personalemappe.
                </p>
              </div>
            </section>

            {/* Modtagere af personoplysninger */}
            <section id="modtagere-personoplysninger" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Modtagere af personoplysninger
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Vi anvender en tredjepart til opbevaring og behandling af data, herunder leverandør af mailklient. I relevante tilfælde kan personoplysninger videregives til andre dataansvarlige, herunder: offentlige myndigheder, banker, inkasso, revisor og advokater.
                </p>
              </div>
            </section>

            {/* Dine rettigheder */}
            <section id="dine-rettigheder" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Dine rettigheder
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Nedenfor kan du læse om hvilke rettigheder du har når vi har registreret dine personoplysninger.
                </p>
                <p>
                  Hvis du ønsker at gøre brug af en af nedenstående rettigheder, kan du kontakte os på oplysningerne der findes ovenfor under ”Kontaktoplysninger”.
                </p>
                
                <div className="space-y-6 mt-6">
                  <div>
                    <h4 className="text-base font-bold text-[#0f0d0e] mb-1">Retten til at trække samtykke tilbage</h4>
                    <p>Hvis en behandling af personoplysninger is baseret på samtykke har du til enhver tid ret til at trække dit samtykke tilbage.</p>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-[#0f0d0e] mb-1">Ret til at se oplysninger (indsigtsret)</h4>
                    <p>Du har ret til at få indsigt i de oplysninger, som vi behandler om dig, samt en række yderligere oplysninger.</p>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-[#0f0d0e] mb-1">Ret til berigtigelse (rettelse)</h4>
                    <p>Du har ret til at få urigtige oplysninger om dig selv rettet.</p>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-[#0f0d0e] mb-1">Ret til sletning</h4>
                    <p>I særlige tilfælde har du ret til at få slettet oplysninger om dig, inden tidspunktet for vores almindelige generelle sletning indtræffer.</p>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-[#0f0d0e] mb-1">Ret til begrænsning af behandling</h4>
                    <p>Du har visse tilfælde ret til at få behandlingen af dine personoplysninger begrænset. Hvis du har ret til at få begrænset behandlingen, må vi fremover kun behandle oplysningerne – bortset fra opbevaring – med dit samtykke, eller med henblik på at retskrav kan fastlægges, gøres gældende eller forsvares, eller for at beskytte en person eller vigtige samfundsinteresser.</p>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-[#0f0d0e] mb-1">Ret til indsigelse</h4>
                    <p>Du har i visse tilfælde ret til at gøre indsigelse mod vores eller lovlige behandling af dine personoplysninger. Du kan også gøre indsigelse hvis dine oplysninger behandles til direkte markedsføring.</p>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-[#0f0d0e] mb-1">Ret til at transmittere oplysninger (dataportabilitet)</h4>
                    <p>Du har i visse tilfælde ret til at modtage dine personoplysninger i et struktureret, almindeligt anvendt og maskinlæsbart format samt at få overført disse personoplysninger fra én dataansvarlig til en anden uden hindring. Du kan læse mere om dine rettigheder i Datatilsynets vejledning om de registreredes rettigheder, som du finder på <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-[#67aedd] hover:underline">www.datatilsynet.dk</a>.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Klage til Datatilsynet */}
            <section id="klage-datatilsynet" className="mb-12 scroll-mt-32">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#0f0d0e] mb-4 pb-2 border-b border-stone-100">
                Klage til Datatilsynet
              </h2>
              <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
                <p>
                  Du har ret til at indgive en klage til Datatilsynet, hvis du er utilfreds med den måde, vi behandler dine personoplysninger på. Du finder Datatilsynets kontaktoplysninger på <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-[#67aedd] hover:underline">www.datatilsynet.dk</a>
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}
